import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FilePdf, Download, Receipt } from "@phosphor-icons/react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/fmt";

/**
 * Phase 26 — "Mes documents" block shown to the authenticated client.
 *
 * Reads only `/api/client/invoices` — strictly the invoices issued to the
 * current user's email. Never shows other clients' data. The backend scopes
 * by email (case insensitive), and the PDF download endpoint enforces the
 * same rule.
 */
function fmtEur(n) {
  const v = Number(n || 0);
  return `${v.toFixed(2)} €`;
}

const STATUS_LABEL = {
  issued: "Émise", sent: "Envoyée", paid: "Payée", draft: "Brouillon",
};

async function downloadInvoice(inv) {
  try {
    const res = await api.get(`/client/invoices/${inv.id}/pdf`, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${inv.invoice_number || "facture"}.pdf`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Échec du téléchargement");
  }
}

export default function MyInvoicesCard() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/client/invoices");
      setInvoices(data || []);
    } catch {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  if (loading && invoices.length === 0) return null;
  if (invoices.length === 0) return null;

  return (
    <div data-testid="client-invoices-card" className="bg-[#121212] border border-zinc-800 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Receipt size={16} className="text-amber-400" />
        <p className="text-sm font-medium text-white">Mes factures</p>
        <span className="text-[11px] text-zinc-500">({invoices.length})</span>
      </div>
      <div className="space-y-2">
        {invoices.map((inv) => (
          <div key={inv.id} data-testid={`client-invoice-${inv.id}`}
            className="bg-[#0f0f0f] border border-zinc-800 rounded-md p-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <FilePdf size={14} className="text-amber-300 shrink-0" />
                <span className="text-xs font-mono text-amber-300">{inv.invoice_number}</span>
                <span className="text-[10px] text-zinc-500">{fmtDate(inv.issue_date)}</span>
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider">{STATUS_LABEL[inv.status] || inv.status}</span>
              </div>
              <p className="text-xs text-zinc-300 mt-0.5">{fmtEur(inv.amount_ttc)} TTC · échéance {fmtDate(inv.due_date) || "—"}</p>
            </div>
            <Button data-testid={`client-invoice-download-${inv.id}`} onClick={() => downloadInvoice(inv)}
              variant="outline" className="h-8 px-2 text-xs border-amber-500/30 text-amber-200 hover:bg-amber-500/10">
              <Download size={12} className="mr-1" /> Télécharger la facture
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
